/** Callback invoked with the error when sync fails irrecoverably */
export type SyncFailureCallback = (error: unknown) => void;

export type TranscriptSyncManagerArgs = {
  meetingId: string;
  patchTranscript: TranscriptSyncManager['patchTranscript'];
  onFailure: SyncFailureCallback;
  currentOffset: number;
  meetingExistsInBackend?: boolean;
  trace?: TranscriptSyncTraceSink;
  pendingTranscript?: PendingTranscript;
  onProgress?: () => void;
  /** Errors after which the batch is resent after `retryDelayMs`; default none. */
  isRetryableError?: (error: unknown) => boolean;
  retryDelayMs?: number;
  onUnsyncedChange?: (unsynced: boolean) => void;
  unsyncedWarningMs?: number;
};

const DEFAULT_RETRY_DELAY_MS = 5_000;
const DEFAULT_UNSYNCED_WARNING_MS = 30_000;

type Delta = {
  speaker: string;
  text: string;
};

export type PendingTranscript = {
  currentOffset: number;
  deltas: Delta[];
  retryBatch?: Delta;
};

export type TranscriptSyncTraceMetadata = {
  chunkId?: number;
};

type PendingDelta = {
  syncDelta: Delta;
  traceMetadata?: TranscriptSyncTraceMetadata;
};

export type TranscriptSyncTraceEvent =
  | {
      type: 'backend_transcript_delta_queued';
      meetingId: string;
      meetingExistsInBackend: boolean;
      pendingCountBeforeAdd: number;
      syncDelta: Delta;
      traceMetadata?: TranscriptSyncTraceMetadata;
    }
  | {
      type: 'backend_transcript_patch_request';
      meetingId: string;
      startOffset: number;
      speaker: string;
      delta: string;
      chunkIds: Array<number | null>;
      sourceDeltas: PendingDelta[];
    };

export type TranscriptSyncTraceSink = (event: TranscriptSyncTraceEvent) => void;

/**
 * Manages transcript delta synchronization between CK and backend.
 */
export class TranscriptSyncManager {
  private meetingId: string;
  private currentOffset: number;
  private onFailure: SyncFailureCallback;
  private patchTranscript: (
    meetingId: string,
    startOffset: number,
    delta: string,
    speaker: string,
    signal?: AbortSignal
  ) => Promise<{ current_length: number; current_speaker: string | null }>;

  private pendingDeltas: PendingDelta[] = [];
  private retryBatch: Delta | undefined;
  private keepFlushingPromise: Promise<void> | null = null;
  private _meetingExistsInBackend: boolean;
  private trace?: TranscriptSyncTraceSink;
  private onProgress?: () => void;
  private isRetryableError: (error: unknown) => boolean;
  private retryDelayMs: number;
  private retriesStopped = false;
  private cancelRetryWait: (() => void) | undefined;
  private inFlightPatch: AbortController | undefined;
  private onUnsyncedChange?: (unsynced: boolean) => void;
  private unsyncedWarningMs: number;
  private unsyncedWarningTimer: ReturnType<typeof setTimeout> | undefined;
  private reportedUnsynced = false;

  get meetingExistsInBackend() {
    return this._meetingExistsInBackend;
  }

  constructor({
    meetingId,
    patchTranscript,
    onFailure,
    currentOffset,
    meetingExistsInBackend = false,
    trace,
    pendingTranscript,
    onProgress,
    isRetryableError = () => false,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
    onUnsyncedChange,
    unsyncedWarningMs = DEFAULT_UNSYNCED_WARNING_MS,
  }: TranscriptSyncManagerArgs) {
    this.meetingId = meetingId;
    this.patchTranscript = patchTranscript;
    this.onFailure = onFailure;
    this.currentOffset = pendingTranscript?.currentOffset ?? currentOffset;
    this.pendingDeltas =
      pendingTranscript?.deltas.map((syncDelta) => ({ syncDelta })) ?? [];
    this.retryBatch = pendingTranscript?.retryBatch;
    this._meetingExistsInBackend = meetingExistsInBackend;
    this.trace = trace;
    this.onProgress = onProgress;
    this.isRetryableError = isRetryableError;
    this.retryDelayMs = retryDelayMs;
    this.onUnsyncedChange = onUnsyncedChange;
    this.unsyncedWarningMs = unsyncedWarningMs;
  }

  /** Makes the next retryable error final and aborts the in-flight patch or retry wait; the unsent batch stays pending. */
  stopRetrying(): void {
    this.retriesStopped = true;
    this.stopUnsyncedWarningTimer();
    this.inFlightPatch?.abort();
    this.cancelRetryWait?.();
  }

  /**
   * Mark the meeting as existing in the backend.
   * This unblocks flushing of pending deltas.
   */
  markMeetingAsExistingInBackend(): void {
    this._meetingExistsInBackend = true;
    void this.keepFlushing().catch(() => {});
  }

  /** Code-point backend length the last patch reported (or the seeded resume
   * offset). Read after the stop flush to capture the round's `expectedLength`
   * for the finalization replace guard. */
  getCurrentOffset(): number {
    return this.currentOffset;
  }

  getPendingTranscript(): PendingTranscript | undefined {
    if (this.pendingDeltas.length === 0 && !this.retryBatch) return undefined;
    return {
      currentOffset: this.currentOffset,
      retryBatch: this.retryBatch,
      deltas: this.pendingDeltas.map(({ syncDelta }) => syncDelta),
    };
  }

  /**
   * Add a delta to the pending queue.
   * Immediately triggers sync if not already in progress.
   */
  addDelta(delta: Delta, traceMetadata?: TranscriptSyncTraceMetadata): void {
    const pendingDelta: PendingDelta = { syncDelta: delta };
    if (traceMetadata) {
      pendingDelta.traceMetadata = traceMetadata;
    }

    const queuedEvent: TranscriptSyncTraceEvent = {
      type: 'backend_transcript_delta_queued',
      meetingId: this.meetingId,
      meetingExistsInBackend: this.meetingExistsInBackend,
      pendingCountBeforeAdd: this.pendingDeltas.length,
      syncDelta: delta,
    };
    if (traceMetadata) {
      queuedEvent.traceMetadata = traceMetadata;
    }

    this.trace?.(queuedEvent);
    this.pendingDeltas.push(pendingDelta);

    void this.keepFlushing().catch(() => {});
  }

  /**
   * If there's any pending deltas, this will send them. And after it sends the pending deltas,
   * it checks there's anymore, and awaits them.
   *
   * Only one instance of this function runs at a time. Attempting to call this function
   * while an existing instance is running just returns the currently running instance.
   *
   * Will not flush until the meeting exists in the backend.
   */
  async keepFlushing(): Promise<void> {
    if (!this.meetingExistsInBackend) {
      return;
    }

    // The last batch may already be in flight even when the queue is empty
    if (this.keepFlushingPromise) return await this.keepFlushingPromise;

    if (this.pendingDeltas.length === 0 && !this.retryBatch) {
      return;
    }

    const self = this;

    async function func() {
      while (self.pendingDeltas.length > 0 || self.retryBatch) {
        // Collect deltas with the same speaker (stop at first speaker change)
        const speaker =
          self.retryBatch?.speaker ??
          self.pendingDeltas[0]?.syncDelta.speaker ??
          'Unknown';
        const deltasToSend: PendingDelta[] = [];

        while (
          !self.retryBatch &&
          self.pendingDeltas.length > 0 &&
          self.pendingDeltas[0]?.syncDelta.speaker === speaker
        ) {
          deltasToSend.push(self.pendingDeltas.shift()!);
        }

        /*
         * Important: the separator used here must match the one the backend
         * uses to concatenate deltas.
         */
        const combinedText =
          self.retryBatch?.text ??
          deltasToSend
            .map((pendingDelta) => pendingDelta.syncDelta.text)
            .join(' ');

        // Keep the exact request in crash snapshots until its response arrives
        self.retryBatch = { speaker, text: combinedText };
        self.trace?.({
          type: 'backend_transcript_patch_request',
          meetingId: self.meetingId,
          startOffset: self.currentOffset,
          speaker,
          delta: combinedText,
          chunkIds: deltasToSend.map(
            (pendingDelta) => pendingDelta.traceMetadata?.chunkId ?? null
          ),
          sourceDeltas: deltasToSend,
        });
        let result: Awaited<ReturnType<typeof self.patchTranscript>>;
        self.inFlightPatch = new AbortController();
        self.startUnsyncedWarningTimer();
        try {
          result = await self.patchTranscript(
            self.meetingId,
            self.currentOffset,
            combinedText,
            speaker,
            self.inFlightPatch.signal
          );
        } catch (error) {
          self.inFlightPatch = undefined;
          if (self.retriesStopped || !self.isRetryableError(error)) throw error;
          console.warn(
            '[TranscriptSyncManager] Transcript patch failed, retrying:',
            error
          );
          await self.waitBeforeRetry(error);
          continue;
        }

        self.inFlightPatch = undefined;
        self.currentOffset = result.current_length;
        self.retryBatch = undefined;
        self.markSynced();
        self.onProgress?.();
      }
    }

    this.keepFlushingPromise = func();
    try {
      await this.keepFlushingPromise;
    } catch (error) {
      this.stopUnsyncedWarningTimer();
      this.onFailure(error);
      throw error;
    } finally {
      this.keepFlushingPromise = null;
    }
  }

  private startUnsyncedWarningTimer() {
    this.unsyncedWarningTimer ??= setTimeout(() => {
      this.reportedUnsynced = true;
      this.onUnsyncedChange?.(true);
    }, this.unsyncedWarningMs);
  }

  private stopUnsyncedWarningTimer() {
    clearTimeout(this.unsyncedWarningTimer);
    this.unsyncedWarningTimer = undefined;
  }

  private markSynced() {
    this.stopUnsyncedWarningTimer();
    if (!this.reportedUnsynced) return;
    this.reportedUnsynced = false;
    this.onUnsyncedChange?.(false);
  }

  private waitBeforeRetry(error: unknown): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.cancelRetryWait = undefined;
        resolve();
      }, this.retryDelayMs);
      this.cancelRetryWait = () => {
        clearTimeout(timer);
        this.cancelRetryWait = undefined;
        reject(error);
      };
    });
  }
}
